import { concatBuffers } from '../concat-buffers';
import { validateChunkSize } from './common-validators';
import { MAX_CONCURRENT_CHUNKS } from './downloaders.constants';
const calculateRequiredChunks = (fileSize, chunkSize) => {
    if (fileSize <= 0n) {
        return 0;
    }
    const chunkSizeBigInt = BigInt(chunkSize);
    return Number((fileSize + chunkSizeBigInt - 1n) / chunkSizeBigInt);
};
/**
 * Downloads a file in concurrent chunks and yields them in order.
 *
 * Yields a single `Uint8Array` per pull, formed by concatenating every
 * consecutive ready chunk available at that moment. The loop dispatches up to
 * `maxConcurrentChunks` downloads at a time and only tops the window up after
 * each yield, so backpressure is structural: while the consumer is awaiting
 * its `sendChunk`, no new downloads start, but in-flight ones still finish and
 * pile up in `readyChunks` for the next yield.
 *
 * Cancellation: break out of the for-await loop. In-flight downloads finish
 * silently into two `Map`s that the runtime then garbage collects with
 * the generator — there is no AbortSignal plumbing yet.
 *
 * Empty files are not supported — check `fileSize > 0n` before calling.
 */
export async function* streamFileChunksConcurrently({ chunkSize, downloadChunk, fileSize, maxConcurrentChunks = MAX_CONCURRENT_CHUNKS, }) {
    if (!Number.isInteger(maxConcurrentChunks) || maxConcurrentChunks <= 0) {
        throw new Error('maxConcurrentChunks must be an integer greater than 0');
    }
    validateChunkSize(chunkSize);
    if (fileSize <= 0n) {
        throw new Error('fileSize must be greater than 0');
    }
    const totalChunksRequired = calculateRequiredChunks(fileSize, chunkSize);
    const readyChunks = new Map();
    const inflight = new Map();
    let nextChunkIndex = 0;
    let nextOffsetToYield = 0n;
    let downloadError;
    const dispatch = (chunkIndex) => {
        const offset = BigInt(chunkIndex) * BigInt(chunkSize);
        const remainingBytes = fileSize - offset;
        const bytesToDownload = Number(remainingBytes < BigInt(chunkSize)
            ? remainingBytes
            : BigInt(chunkSize));
        const task = (async () => {
            try {
                const buffer = new Uint8Array(bytesToDownload);
                const dataReadSize = await downloadChunk(buffer, offset);
                readyChunks.set(offset, dataReadSize < bytesToDownload
                    ? buffer.slice(0, dataReadSize)
                    : buffer);
            }
            catch (error) {
                downloadError =
                    error instanceof Error ? error : new Error(String(error));
            }
            finally {
                inflight.delete(offset);
            }
        })();
        inflight.set(offset, task);
    };
    while (nextOffsetToYield < fileSize) {
        if (downloadError !== undefined) {
            throw downloadError;
        }
        // Top up the concurrency window. New downloads only start here, so
        // while the consumer is awaiting `sendChunk` (generator suspended at
        // yield) no new ones are launched.
        while (inflight.size < maxConcurrentChunks &&
            nextChunkIndex < totalChunksRequired) {
            dispatch(nextChunkIndex++);
        }
        if (!readyChunks.has(nextOffsetToYield)) {
            // Wait for any in-flight download to finish, then re-check at the
            // top of the loop (which also picks up any downloadError).
            await Promise.race(inflight.values());
            continue;
        }
        // Greedily collect every consecutive ready chunk.
        const batch = [];
        let cursor = nextOffsetToYield;
        while (readyChunks.has(cursor)) {
            const chunk = readyChunks.get(cursor);
            batch.push(chunk);
            readyChunks.delete(cursor);
            cursor += BigInt(chunk.byteLength);
        }
        nextOffsetToYield = cursor;
        yield concatBuffers(batch);
    }
}
